import { createContext, useContext, useState, useEffect, useCallback, useMemo, ReactNode } from "react";
import { User, Session } from "@supabase/supabase-js";
import * as Sentry from "@sentry/react";
import { supabase } from "@/integrations/supabase/client";
import { fetchProfileWithSubscription, updateProfile as updateProfileDb, type UserEditableProfileUpdate } from "@/services/db/profiles";
import { ANALYTICS_EVENTS } from "@shared/schemas/analytics";
import { captureEvent, identifyUser, resetAnalytics } from "@/lib/analyticsQueue";
import { trackMetaEvent } from "@/lib/metaPixel";
import { apiPost } from "@/lib/api/apiClient";
import { clearAllFlowDrafts } from "@/lib/flowDraftCleanup";
import { queryClient } from "@/lib/queryClient";
import { MARKETING_CONSENT_TEXT_VERSION } from "@/config/consent";
import { SENTRY_TAGS, SENTRY_ERROR_TYPES } from "@/config/sentry";
import { toast } from "@/lib/toast";
import { getCurrentTranslations } from "@/lib/i18n/translations";
import {
  getStoredReferralCode,
  clearStoredReferralCode,
} from "@/lib/referral/captureReferralCode";
import type { TrackSignupResponse } from "@shared/schemas/api";

/**
 * localStorage key that tracks the last signed-in user-id on this browser.
 * Used to detect cross-user state leakage (e.g. anonymous visitor →
 * fresh-signup, or sign-out → sign-in as a different account on a shared
 * device) and wipe persisted flow drafts before they cause downstream
 * namespace errors. See the `onAuthStateChange` callback for details.
 */
const LAST_SIGNED_IN_USER_KEY = "auth.lastSignedInUserId";

export interface Profile {
  id: string;
  user_id: string;
  display_name: string | null;
  avatar_url: string | null;
  subscription_id: string | null;
  subscription_name?: string;
  credit_limit?: number;
  billing_period?: string;
  subscription_status?: string;
  current_period_start?: string;
  current_period_end?: string;
  cancelled_plan_id?: string;
  discount_percent?: number;
  discount_charges_remaining?: number;
  scheduled_plan_id?: string;
  scheduled_billing_period?: string;
  was_pro?: boolean;
  onboarding_completed?: boolean;
  cookie_consent_analytics?: boolean | null;
  cookie_consent_timestamp?: string | null;
  marketing_consent?: boolean;
  marketing_consent_at?: string | null;
  marketing_consent_ip?: string | null;
  marketing_consent_text_version?: string | null;
  marketing_consent_declined_at?: string | null;
  role?: string;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  profile: Profile | null;
  loading: boolean;
  refreshProfile: () => Promise<void>;
  signInWithGoogle: () => Promise<{ error: Error | null }>;
  signUp: (
    email: string,
    password: string,
    displayName: string | undefined,
    marketingConsent: boolean,
    marketingConsentIp: string | null,
  ) => Promise<{ error: Error | null; session: Session | null }>;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
  // MF-6 (validation): typed `UserEditableProfileUpdate` so TypeScript actually
  // flags callers that try to write server-only columns (`role`, `was_pro`,
  // `referral_code`, `subscription_id`). Runtime filter in `updateProfileDb`
  // is still the security boundary; this is defense-in-depth at compile time.
  updateProfile: (updates: UserEditableProfileUpdate) => Promise<{ error: Error | null }>;
  // Opens once per device for genuinely new Google-OAuth signups so they get
  // the same marketing-consent ask that email-signup users see at the form.
  // Email-signup users never trigger this — they consented (or declined) in
  // the form itself, which writes through raw_user_meta_data on first insert.
  marketingPromptOpen: boolean;
  dismissMarketingPrompt: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const [marketingPromptOpen, setMarketingPromptOpen] = useState(false);

  // Fetch user profile with subscription info
  const fetchProfile = useCallback(async (userId: string): Promise<Profile | null> => {
    try {
      return await fetchProfileWithSubscription(userId) as Profile | null;
    } catch (error) {
      // Previously swallowed via `console.error`, which left a real Supabase
      // outage invisible to Sentry while the UI silently proceeded with a
      // null profile (cascading into "free plan" rendering, missing
      // marketing-consent prompt, etc.). Capture with a typed tag so this is
      // alertable, and preserve the existing "return null" contract.
      Sentry.captureException(error, {
        tags: { [SENTRY_TAGS.TYPE]: SENTRY_ERROR_TYPES.PROFILE_FETCH_FAILURE },
      });
      return null;
    }
  }, []);

  useEffect(() => {
    // Set up auth state listener BEFORE checking session
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);

        if (session?.user) {
          // Wipe persisted flow drafts when the signed-in user differs from
          // the last known user on this browser. Protects fresh signups from
          // inheriting a previous visitor's IndexedDB state (preview URLs,
          // uploads) — the cause of "Image URL is not in an allowed namespace
          // for this user" rejections seconds after a Google-OAuth signup.
          // User-scoped contexts (Credits, GeneratedImages) and user-keyed
          // React Query entries re-fetch automatically when `user` changes,
          // so no full-page reload is needed here.
          let lastUserId: string | null = null;
          try {
            lastUserId = localStorage.getItem(LAST_SIGNED_IN_USER_KEY);
          } catch {
            // ignore localStorage failures (private mode, etc.)
          }
          if (lastUserId !== session.user.id) {
            try {
              localStorage.setItem(
                LAST_SIGNED_IN_USER_KEY,
                session.user.id,
              );
            } catch {
              // ignore localStorage failures
            }
            // Only wipe on a genuine account switch (a different
            // *already-authenticated* user on this device). A null lastUserId
            // means the prior session was anonymous — the draft belongs to the
            // person now signing in, so preserve it (the anonymous→signup
            // handoff useFlowPersistence is built for; wiping here was
            // destroying their in-progress flow selections at the moment of
            // login). Anonymous drafts only ever hold local File blobs (uploads
            // stay publicUrl:"" until generate(), which is behind the auth
            // gate), never user-scoped R2 URLs, so skipping the wipe here
            // cannot reintroduce the "namespace not allowed" rejection.
            if (lastUserId !== null) {
              await clearAllFlowDrafts();
            }
          }

          // Detect new Google OAuth users: SIGNED_IN event + account created within 60s
          const isNewOAuthUser =
            event === "SIGNED_IN" &&
            session.user.app_metadata?.provider === "google" &&
            session.user.created_at &&
            Date.now() - new Date(session.user.created_at).getTime() < 60_000;

          // New users' signup is tracked server-side via /api/auth/track-signup
          // (below) so it bypasses cookie consent and is not double-counted.
          // Existing Google users still get a client-side sign-in event.
          if (
            event === "SIGNED_IN" &&
            session.user.app_metadata?.provider === "google" &&
            !isNewOAuthUser
          ) {
            captureEvent(ANALYTICS_EVENTS.USER_SIGN_IN, { method: "google" });
          }

          fetchProfile(session.user.id).then((p) => {
            setProfile(p);
            identifyUser(session.user.id, {
              email: session.user.email,
              display_name: p?.display_name,
              subscription_name: p?.subscription_name,
            });
            Sentry.setUser({ id: session.user.id, email: session.user.email });
          });

          // Fire server-side sign-up event for genuinely new users (bypasses cookie consent).
          //
          // Dedup guard: Supabase fires SIGNED_IN multiple times during the
          // signup flow AND broadcasts the event to every tab on the same
          // origin via BroadcastChannel. Each listener fire would otherwise
          // reinvoke the CompleteRegistration Meta Pixel event AND the
          // server-side /track-signup endpoint.
          //
          // First pass used sessionStorage, which collapsed 4× → 2× in prod —
          // sessionStorage is per-tab, so a second open tab still fired once.
          // localStorage is shared across tabs on the same origin: the first
          // tab to handle SIGNED_IN writes the flag, all other tabs see it
          // immediately. Re-fires only for a different user_id (so a second
          // user signing up on a shared device still tracks correctly).
          const accountAge = session.user.created_at
            ? Date.now() - new Date(session.user.created_at).getTime()
            : Infinity;
          const signupFiredKey = `flowly-signup-fired-${session.user.id}`;
          let alreadyFired = false;
          try {
            alreadyFired = localStorage.getItem(signupFiredKey) === "1";
          } catch {
            // private mode / quota — fail open so a real signup is never lost
          }
          if (
            event === "SIGNED_IN" &&
            accountAge < 60 * 60 * 1000 &&
            !alreadyFired
          ) {
            try {
              localStorage.setItem(signupFiredKey, "1");
            } catch {
              // ignore localStorage failures (private mode, quota, etc.)
            }
            const method = session.user.app_metadata?.provider === "google" ? "google" : "email";
            const referralCode = getStoredReferralCode() ?? undefined;
            // Meta Pixel CompleteRegistration. eventID enables Meta's own
            // server-side deduplication — same eventID arriving within ~7
            // days collapses to one count. This is the safety net for cases
            // localStorage can't cover: email-confirmation links that open
            // in a different browser context (Gmail in-app webview, default
            // browser launched by Mail, mobile vs desktop), where each
            // context has an independent localStorage and would otherwise
            // fire the event again. The id is deterministic per registered
            // user_id so any duplicate originating from the same signup
            // converges on the same Meta event.
            const signupEventID = `signup-${session.user.id}`;
            trackMetaEvent(
              "CompleteRegistration",
              { method },
              { eventID: signupEventID },
            );
            apiPost<TrackSignupResponse>("/api/auth/track-signup", { method, referralCode })
              .then((res) => {
                // Resolved either way — clear the stored code so it isn't reused.
                clearStoredReferralCode();
                if (res.referral?.attributed) {
                  const tr = getCurrentTranslations();
                  const credits = String(res.referral.refereeBonusCredits ?? "");
                  const referrer = res.referral.referrerName;
                  // Nudge: the bonus is deferred to a paid subscription, so no
                  // balance changes here — just inform the referred friend that
                  // subscribing unlocks credits for both of them.
                  toast.success(
                    referrer
                      ? tr.referral.bonusToast
                          .replace("{credits}", credits)
                          .replace("{name}", referrer)
                      : tr.referral.bonusToastGeneric.replace("{credits}", credits),
                  );
                }
              })
              .catch((err) => {
                // Non-critical for the signup flow itself, but a sustained
                // failure here means dropped referral attribution (the bonus
                // credits never get granted). Capture so it's alertable.
                Sentry.captureException(err, {
                  tags: { [SENTRY_TAGS.TYPE]: SENTRY_ERROR_TYPES.TRACK_SIGNUP_FAILURE },
                });
              });
          }
        } else {
          setProfile(null);
          Sentry.setUser(null);
        }
      }
    );

    // Check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);

      if (session?.user) {
        fetchProfile(session.user.id).then(setProfile);
      }
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, [fetchProfile]);

  const signUp = useCallback(async (
    email: string,
    password: string,
    displayName: string | undefined,
    marketingConsent: boolean,
    marketingConsentIp: string | null,
  ) => {
    // Return to the page the user was on so their flow state (persisted in
    // localStorage + IndexedDB by useFlowPersistence — shared across tabs)
    // rehydrates after they confirm their email and click back into the app.
    const returnPath = `${window.location.pathname}${window.location.search}`;
    const { error, data } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: `${window.location.origin}${returnPath}`,
        // These fields are read by the public.handle_new_user() trigger and
        // persisted onto the profiles row as the consent audit trail.
        data: {
          display_name: displayName,
          marketing_consent: marketingConsent,
          marketing_consent_text_version: MARKETING_CONSENT_TEXT_VERSION,
          marketing_consent_ip: marketingConsentIp,
          // Carried into auth.users.raw_user_meta_data so referral attribution
          // survives email confirmation on another device.
          referral_code: getStoredReferralCode() ?? undefined,
        },
      },
    });
    // Signup is tracked server-side via /api/auth/track-signup (see
    // onAuthStateChange) — not captured here, to avoid double-counting.
    return { error, session: data.session };
  }, []);

  const signIn = useCallback(async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    if (!error) {
      captureEvent(ANALYTICS_EVENTS.USER_SIGN_IN, { method: "email" });
    }
    return { error };
  }, []);

  const signInWithGoogle = useCallback(async () => {
    // Note: sign-up vs sign-in detection happens in onAuthStateChange
    // after the OAuth redirect, where we can check user.created_at.
    // We bounce back to the page the user was on so flow state persisted in
    // sessionStorage rehydrates onto the same studio flow they came from.
    const returnPath = `${window.location.pathname}${window.location.search}`;
    const { error } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: {
        redirectTo: `${window.location.origin}${returnPath}`,
      },
    });
    return { error };
  }, []);

  const signOut = useCallback(async () => {
    captureEvent(ANALYTICS_EVENTS.USER_SIGN_OUT);
    // Wipe in-progress flow drafts so they don't leak to the next user on a
    // shared device (config in localStorage + uploaded images in IndexedDB).
    await clearAllFlowDrafts();
    await supabase.auth.signOut();
    resetAnalytics();
    Sentry.setUser(null);
    // H-1 (security audit): drop React Query cache so the next user on a
    // shared device doesn't inherit the previous user's cached query data
    // (BillingTab payments, credit transactions, etc. keyed by userId).
    queryClient.clear();
    setUser(null);
    setSession(null);
    setProfile(null);
  }, []);

  const updateProfile = useCallback(async (updates: UserEditableProfileUpdate) => {
    if (!user) return { error: new Error("No user logged in") };

    try {
      await updateProfileDb(user.id, updates);
      const updatedProfile = await fetchProfile(user.id);
      setProfile(updatedProfile);
      return { error: null };
    } catch (error) {
      return { error: error as Error };
    }
  }, [user, fetchProfile]);

  const refreshProfile = useCallback(async () => {
    if (!user) return;
    const updatedProfile = await fetchProfile(user.id);
    setProfile(updatedProfile);
  }, [user, fetchProfile]);

  // Open/close the marketing-consent modal purely from the loaded profile.
  // The modal blocks the UI until the user either joins (sets
  // marketing_consent=true) or declines (sets marketing_consent_declined_at).
  // Once either column flips, this effect re-runs and closes the modal.
  useEffect(() => {
    if (!profile) {
      setMarketingPromptOpen(false);
      return;
    }
    const undecided =
      profile.marketing_consent === false &&
      !profile.marketing_consent_declined_at;
    setMarketingPromptOpen(undecided);
  }, [profile]);

  const dismissMarketingPrompt = useCallback(async () => {
    // Persist the decline in the DB (not localStorage) so it survives across
    // devices/sessions. updateProfile refetches the profile internally, and
    // the effect above then closes the modal automatically.
    await updateProfile({
      marketing_consent_declined_at: new Date().toISOString(),
    });
  }, [updateProfile]);

  const value = useMemo(() => ({
    user,
    session,
    profile,
    loading,
    refreshProfile,
    signUp,
    signIn,
    signInWithGoogle,
    signOut,
    updateProfile,
    marketingPromptOpen,
    dismissMarketingPrompt,
  }), [user, session, profile, loading, refreshProfile, signUp, signIn, signInWithGoogle, signOut, updateProfile, marketingPromptOpen, dismissMarketingPrompt]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
